// Master Apps · Aguardando liberação — conta pendente de aprovação do admin
// (variante pending) ou recusada (variante rejected). Página cheia, sem
// sidebar: fica sob RequireAuth, fora do AccessGate.
import { type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  AuthCardBody,
  AuthShell,
  primaryButtonStyle,
  SUPPORT_URL,
  secondaryButtonStyle,
  useAuthSignOut,
} from '@/components/auth-shell';
import { IconClock, IconLock, IconLogout, IconX } from '@/components/icons';
import { ScreenLoading } from '@/components/loading';
import { useMeFresh } from '@/hooks/use-applications';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { fontMono, fontSans, makePalette } from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

export function AguardandoPage() {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  const vp = useViewport();
  const navigate = useNavigate();
  const handleSignOut = useAuthSignOut('aguardando');

  const meQuery = useMeFresh();
  const me = meQuery.data;

  // Auto-redirect: aprovado não fica aqui. Sem matrícula vai onboardar; com
  // matrícula vai para o catálogo. Só decide com dado fresco (isFetching falso).
  useEffect(() => {
    if (!me || meQuery.isFetching) return;
    if (me.status === 'approved') {
      navigate(me.needsOnboarding ? '/primeiro-acesso' : '/catalog', { replace: true });
    }
  }, [me, meQuery.isFetching, navigate]);

  // Os botões da casca têm 11px de respiro vertical (≈40px de altura). No
  // celular sobem para os 44px de alvo de toque; no desktop `auto` é o valor
  // inicial de min-height, então nada muda.
  const alvoToque = { minHeight: byViewport(vp, { mobile: '44px', desktop: 'auto' }) };
  const primary = { ...primaryButtonStyle(sp.accent), ...alvoToque };
  const signOutButton = (
    <button
      type="button"
      onClick={handleSignOut}
      style={{ ...secondaryButtonStyle(p), ...alvoToque }}
    >
      <IconLogout size={14} />
      Sair
    </button>
  );

  // Conteúdo depende do /api/me: sem dado ainda, spinner; erro, retry mínimo.
  let content: ReactNode;
  if (!me) {
    if (!meQuery.isError) return <ScreenLoading dark={sp.dark} />;
    content = (
      <AuthCardBody
        title="Falha ao carregar seu perfil"
        titleSize={20}
        titleTracking="-0.02em"
        description="Não foi possível verificar a situação da sua conta. Tente novamente."
        actions={
          <>
            <button type="button" onClick={() => void meQuery.refetch()} style={primary}>
              Tentar novamente
            </button>
            {signOutButton}
          </>
        }
      />
    );
  } else if (me.status === 'rejected') {
    content = (
      <AuthCardBody
        badge={<IconX size={24} stroke={2} />}
        title="Acesso não autorizado"
        description="Seu acesso foi recusado. Fale com a TI se acredita que é um engano."
        actions={
          <>
            <a href={SUPPORT_URL} target="_blank" rel="noreferrer" style={primary}>
              Falar com a TI
            </a>
            {signOutButton}
          </>
        }
      />
    );
  } else if (me.status === 'inactive') {
    // Desativado pela conciliação com o RH — mensagem própria, e não a de
    // "recusado": quem foi desligado ou está afastado precisa procurar o RH, não
    // a TI. Só têm acesso as matrículas em situação ATIVO ou FÉRIAS; quem chega
    // aqui já TEM matrícula vinculada (sem matrícula o gate manda para o primeiro
    // acesso, não para esta tela).
    content = (
      <AuthCardBody
        badge={<IconLock size={24} stroke={2} />}
        title="Conta desativada"
        description="Sua matrícula não está ativa no RH, então o acesso ao catálogo foi suspenso automaticamente. Se você continua na empresa, procure o RH para regularizar o cadastro — o acesso volta sozinho na próxima sincronização."
        actions={
          <>
            <a href={SUPPORT_URL} target="_blank" rel="noreferrer" style={primary}>
              Falar com a TI
            </a>
            {signOutButton}
          </>
        }
      />
    );
  } else {
    // pending (o AccessGate só manda pending/rejected para cá)
    content = (
      <AuthCardBody
        badge={<IconClock size={24} stroke={2} />}
        title="Conta aguardando liberação"
        description="Seu acesso foi registrado e está aguardando aprovação de um administrador."
        actions={
          <>
            <button
              type="button"
              onClick={() => void meQuery.refetch()}
              disabled={meQuery.isFetching}
              style={{
                ...primary,
                background: meQuery.isFetching ? p.surface3 : sp.accent,
                color: meQuery.isFetching ? p.muted : '#fff',
                cursor: meQuery.isFetching ? 'wait' : 'pointer',
              }}
            >
              {meQuery.isFetching ? 'Verificando…' : 'Verificar novamente'}
            </button>
            {signOutButton}
          </>
        }
      />
    );
  }

  return (
    <AuthShell
      below={
        me?.email && (
          <div
            style={{
              marginTop: 20,
              textAlign: 'center',
              fontSize: 11,
              color: p.muted,
              fontFamily: fontSans,
            }}
          >
            {/* A prosa vai na sans; só o e-mail (identificador) fica na mono. */}
            Conectado como <span style={{ fontFamily: fontMono }}>{me.email}</span>
          </div>
        )
      }
    >
      {content}
    </AuthShell>
  );
}
